<svelte:options
  customElement={{
    tag: "oc-cinema-v1",
    extend: window.__components.extend({}),
    props: {
      arrowButtonPosition: { type: "String", attribute: "arrow-button-position" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      paginationDots: { type: "Boolean", attribute: "pagination-dots" },
      itemVisibilityThreshold: { type: "Array", attribute: "item-visibility-threshold" },
      hideSkipLink: { type: "Boolean", attribute: "hide-skip-link" },
      cut: { type: "String", attribute: "cut" },
      gap: { type: "String", attribute: "gap" },
      itemsPerPage: { type: "String", attribute: "items-per-page" },
      stageSpacing: { type: "String", attribute: "stage-spacing" },
    },
  }}
/>

<script lang="ts">
  import { onMount, tick } from "svelte";
  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use";
  import { onReady } from "@otto-ec/global-resources/dom";
  import { unsetAll } from "@otto-ec/global-resources/event";
  import { pointerType } from "@otto-ec/global-resources/device";
  import { refireScrollEvent } from "../../../common/actions/refireScrollEvent";

  import type { Events, Methods, Props } from "./CinemaV1.types";
  import { DefaultStyles } from "../../../common/components/DefaultStyles";
  import { SkipButton } from "../../../common/components/SkipButton";
  import { ArrowButtonV1 } from "../../../common/components/ArrowButton";
  import { PaginationDotsV1 } from "../../../common/components/PaginationDotsV1";

  import {
    ITEM_VISIBILITY_CHANGED_EVENT,
    STAGE_SCROLL_EVENT,
    useScrollEvents,
    type ScrollSource,
  } from "./lib/events";
  import { type CinemaItemHTMLElement } from "./lib/observer";
  import { noop } from "./lib/utils";
  import { useStageScrollEvent } from "./lib/scrollEvent.svelte";
  import { useFocusInHandler } from "./lib/focusIn";
  import { useStageControlsState } from "./lib/controls.svelte";
  import { useItemState } from "./lib/itemState.svelte";
  import type { MoveToOptions } from "./lib/lib.cinema";
  import {
    moveToInternal,
    useCustomItemVisibilityObserver,
    useMainItemVisibilityObserver,
    useStageMutationObserver,
  } from "./lib/lib.cinema";

  let {
    ocAriaLabel,
    arrowButtonPosition,
    resizing = "stretch-cut",
    paginationDots = false,
    itemVisibilityThreshold = undefined,
    visibleItems = [],
    cut = undefined,
    gap = undefined,
    stageSpacing = undefined,
    itemsPerPage = undefined,
    hideSkipLink = false,
  }: Props = $props();

  const Host = $host<HTMLOcCinemaV1Element>();

  let stage = $state<HTMLElement>();
  let stageScrolling = false;
  let stageScrollSource: "arrows" | "pagination-dots" | "move-method" | "swipe" = "swipe";

  const itemState = useItemState();

  let items = $state(Array.from(Host.children) as CinemaItemHTMLElement[]);

  let stageClientWidth: number = $state(1);

  const allProps = $derived({
    resizing,
    itemsPerPage,
  });
  const getItems = (): HTMLElement[] => items;
  const getStage = (): HTMLElement => stage!;
  const setStageScrolling = (scrolling: boolean): unknown => (stageScrolling = scrolling);
  const setStageScrollSource = (source: ScrollSource): unknown => (stageScrollSource = source);

  const dispatchScrollEvent = useScrollEvents(
    itemState,
    useEventDispatcher<Events, false>(Host, false),
    getItems,
    () => visibleItems,
    () => stageScrolling,
    () => stageScrollSource,
  );

  const scroll = useStageScrollEvent(
    itemState,
    getStage,
    dispatchScrollEvent,
    () => stageScrollSource,
    setStageScrolling,
    setStageScrollSource,
  );

  const controls = useStageControlsState(
    Host,
    () => allProps,
    getItems,
    getStage,
    () => scroll.stageScrollLeft,
    () => stageClientWidth,
    () => $pointerType,
  );

  useFocusInHandler(Host);

  onMount(() => {
    /*                                                            */
    const dispatchScroll = (): void => {
      if (itemState.isEqual()) return;
      dispatchScrollEvent(STAGE_SCROLL_EVENT, ITEM_VISIBILITY_CHANGED_EVENT);
    };

    /*                                     */
    const setVisibleItems = (visible: HTMLElement[]): unknown => (visibleItems = visible);

    /*                                                            */
    const [mainItemVisibilityObserver, disconnectMainObserver] = useMainItemVisibilityObserver(
      itemState,
      tick,
      getItems,
      getStage,
      /*                                       */
      /*                                                        */
      /*                                                                   */
      itemVisibilityThreshold ? noop : dispatchScroll,
      itemVisibilityThreshold ? noop : setVisibleItems,
      controls.setStageScrollWidth,
      setStageScrolling,
    );

    /*                                                                      */
    const [customItemVisibilityObserver, disconnectCustomObserver] =
      useCustomItemVisibilityObserver(
        tick,
        getItems,
        getStage,
        dispatchScroll,
        setVisibleItems,
        itemVisibilityThreshold,
      );

    /*                                                                           */
    const disconnectStageMutationObserver = useStageMutationObserver(
      Host,
      (i) => (items = i),
      mainItemVisibilityObserver,
      customItemVisibilityObserver,
    );

    return unsetAll(
      disconnectMainObserver,
      disconnectCustomObserver,
      disconnectStageMutationObserver,
    );
  });

  export const moveTo = (async (targetSlide, options: MoveToOptions = {}) => {
    /*                                                                            */
    /*                                                           */
    return moveToInternal(
      stage!,
      controls.scrollPoints,
      setStageScrollSource,
      targetSlide,
      options,
    );
  }) satisfies Methods["moveTo"];
</script>

<div
  class="cinema"
  style:--arrow-button-position={arrowButtonPosition}
  style:--cut={cut}
  style:--gap={gap}
  style:--items-per-page={itemsPerPage}
  style:--internal-items-per-page={controls.itemsPerPage()}
  style:--stage-spacing={stageSpacing}
>
  {#if !hideSkipLink && $onReady && items.length > 3}<SkipButton
      text={ocAriaLabel}
      offsetX="var(--gap)"
    />{/if}

  <section
    class={`cinema__stage cinema__stage--${resizing}`}
    class:cinema__stage--stretch={controls.stretchItems()}
    class:cinema__stage--cut={resizing === "stretch-cut" || resizing === "cut"}
    bind:this={stage}
    bind:clientWidth={stageClientWidth}
    use:refireScrollEvent={Host}
    aria-label={ocAriaLabel}
  >
    <slot />
  </section>

  {#if $onReady}
    <ArrowButtonV1
      hidden={controls.hideLeftArrow()}
      direction="left"
      onclick={() => moveTo(controls.currentPage() - 1, { source: "arrows" })}
    />
    <ArrowButtonV1
      hidden={controls.hideRightArrow()}
      direction="right"
      onclick={() => moveTo(controls.currentPage() + 1, { source: "arrows" })}
    />
  {/if}
</div>

{#if $onReady && paginationDots}
  <PaginationDotsV1
    amountDots={controls.scrollPoints().length}
    selectedDot={controls.currentPage()}
    {moveTo}
  />
{/if}

<DefaultStyles display="block" />

<style lang="scss">
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "math/calcItemBase" as *;
  @use "../../floating-focus/v1/FloatingFocusV1.global";
  :global {
    :host {
      /*        */
      --gap: #{tokens.$oc-component-cinema-stage-gap-x};
      --stage-spacing: #{tokens.$oc-component-cinema-stage-spacing-x};
      --cut: #{tokens.$oc-component-cinema-cut-item-visible-percentage};
      --arrow-button-position: 50%;
      --items-per-page: initial;
      position: relative;
    }

    .cinema {
      position: relative;

      &__stage {
        display: flex;
        gap: var(--gap);
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none; /*     */
        scroll-behavior: smooth;

        /*                                  */
        padding: 4px 0;
        margin: -4px 0;

        &::-webkit-scrollbar {
          display: none; /*                */
        }

        /*                                            */
        @media (prefers-reduced-motion) {
          scroll-behavior: auto;
        }

        ::slotted(*) {
          flex: 0 0 auto;
          max-width: 100%;
          min-width: 0;
        }

        &--cut {
          ::slotted(*) {
            flex: 0 0
              calcItemBase(
                var(--items-per-page, var(--internal-items-per-page)),
                var(--gap),
                var(--cut),
                var(--stage-spacing)
              );
          }
        }

        &--stretch {
          ::slotted(*) {
            flex: 1 0 auto;
          }
        }
      }
    }

    /*                                               */
    .cinema__stage--stretch.cinema__stage--cut {
      ::slotted(*) {
        flex: 1 0 0;
      }
    }
  }
</style>
